import { Principle, Difficulty, AIPersonality, PersonalityInfo } from './types';

// App version
export const APP_VERSION = 'v3.2.8';

export const AI_PLAYER_ID = "ai_opponent";
export const AI_PLAYER_NAME = "AI Opponent";

// AI Personality Pantheon
export const PERSONALITIES: Record<AIPersonality, PersonalityInfo> = {
  socrates: {
    id: 'socrates',
    name: 'Socrates',
    subtitle: 'The Questioner',
    difficulty: 'easier',
    description: 'Uses the Socratic method to probe assumptions and seek deeper understanding',
    debatingStyle: 'Questions conventional wisdom, exposes contradictions, dialectical reasoning',
    unlockCriteria: 'Unlocked from start',
    color: 'blue',
  },
  mill: {
    id: 'mill',
    name: 'John Stuart Mill',
    subtitle: 'The Utilitarian',
    difficulty: 'moderate',
    description: 'Focuses on practical consequences and the greatest happiness principle',
    debatingStyle: 'Empirical examples, cost-benefit analysis, social welfare focus',
    unlockCriteria: 'Score 20+ points in any game',
    color: 'green',
  },
  kant: {
    id: 'kant',
    name: 'Immanuel Kant',
    subtitle: 'The Deontologist',
    difficulty: 'moderate',
    description: 'Champions moral duty, universal principles, and categorical imperatives',
    debatingStyle: 'Appeals to moral law, duty-based reasoning, principled arguments',
    unlockCriteria: 'Win a game against Mill',
    color: 'purple',
  },
  rand: {
    id: 'rand',
    name: 'Ayn Rand',
    subtitle: 'The Objectivist',
    difficulty: 'harder',
    description: 'Defends individual rights, rational self-interest, and productive achievement',
    debatingStyle: 'Bold assertions, challenges collectivism, provocative rhetoric',
    unlockCriteria: 'Score 8+ in a single round',
    color: 'red',
  },
  nietzsche: {
    id: 'nietzsche',
    name: 'Friedrich Nietzsche',
    subtitle: 'The Iconoclast',
    difficulty: 'harder',
    description: 'Undermines conventional morality and challenges comfortable assumptions',
    debatingStyle: 'Contrarian perspectives, value inversion, poetic provocation',
    unlockCriteria: 'Win 3 games total',
    color: 'yellow',
  },
  aristotle: {
    id: 'aristotle',
    name: 'Aristotle',
    subtitle: 'The Pragmatist',
    difficulty: 'easier',
    description: 'Focuses on virtue, practical wisdom, and finding the golden mean',
    debatingStyle: 'Balanced reasoning, virtue ethics, real-world applications',
    unlockCriteria: 'Win any game',
    color: 'amber',
  },
  beauvoir: {
    id: 'beauvoir',
    name: 'Simone de Beauvoir',
    subtitle: 'The Existentialist',
    difficulty: 'moderate',
    description: 'Champions freedom, authenticity, and the ethics of ambiguity',
    debatingStyle: 'Existential analysis, freedom-focused arguments, gender critique',
    unlockCriteria: 'Complete 5 games total',
    color: 'pink',
  },
  rawls: {
    id: 'rawls',
    name: 'John Rawls',
    subtitle: 'The Egalitarian',
    difficulty: 'moderate',
    description: 'Advocates for justice as fairness and equality of opportunity',
    debatingStyle: 'Social contract theory, veil of ignorance, fairness principles',
    unlockCriteria: 'Win against 3 different opponents',
    color: 'cyan',
  },
  singer: {
    id: 'singer',
    name: 'Peter Singer',
    subtitle: 'The Utilitarian Ethicist',
    difficulty: 'harder',
    description: 'Applies rigorous consequentialist reasoning to contemporary ethical issues',
    debatingStyle: 'Cost-benefit analysis, challenging intuitions, global perspective',
    unlockCriteria: 'Score 25+ in a single game (win)',
    color: 'emerald',
  },
  foucault: {
    id: 'foucault',
    name: 'Michel Foucault',
    subtitle: 'The Critic',
    difficulty: 'harder',
    description: 'Analyzes power structures and challenges dominant discourse',
    debatingStyle: 'Discourse analysis, power critique, genealogical method',
    unlockCriteria: 'Win 5 games total',
    color: 'violet',
  },
  confucius: {
    id: 'confucius',
    name: 'Confucius',
    subtitle: 'The Ethical Teacher',
    difficulty: 'easier',
    description: 'Champions virtue, ritual propriety, family values, and social harmony',
    debatingStyle: 'Practical examples, tradition, family analogies, social duty',
    unlockCriteria: 'Unlocked from start',
    color: 'orange',
  },
  laozi: {
    id: 'laozi',
    name: 'Laozi',
    subtitle: 'The Mystic',
    difficulty: 'easier',
    description: 'Teaches wu wei (non-action), naturalness, and going with the flow',
    debatingStyle: 'Paradoxes, nature metaphors, simplicity, subtle wisdom',
    unlockCriteria: 'Win any game',
    color: 'teal',
  },
  buddha: {
    id: 'buddha',
    name: 'Buddha',
    subtitle: 'The Enlightened One',
    difficulty: 'moderate',
    description: 'Teaches the Middle Way, impermanence, and liberation from suffering',
    debatingStyle: 'Mindfulness, compassion, questioning attachments, practical wisdom',
    unlockCriteria: 'Score 20+ points in any game',
    color: 'indigo',
  },
  sun_tzu: {
    id: 'sun_tzu',
    name: 'Sun Tzu',
    subtitle: 'The Strategist',
    difficulty: 'moderate',
    description: 'Applies strategic thinking, indirect approaches, and tactical wisdom',
    debatingStyle: 'Strategic examples, indirect arguments, positioning, adaptability',
    unlockCriteria: 'Win against 2 different opponents',
    color: 'slate',
  },
  mencius: {
    id: 'mencius',
    name: 'Mencius',
    subtitle: 'The Idealist',
    difficulty: 'moderate',
    description: 'Argues for innate human goodness, moral cultivation, and benevolent governance',
    debatingStyle: 'Human nature arguments, moral intuition, practical governance examples',
    unlockCriteria: 'Complete 5 games total',
    color: 'lime',
  },
  rumi: {
    id: 'rumi',
    name: 'Rumi',
    subtitle: 'The Mystic Poet',
    difficulty: 'moderate',
    description: 'Explores divine love, unity, and transcendence through poetic wisdom',
    debatingStyle: 'Poetic metaphors, love and unity, mystical insights, paradox',
    unlockCriteria: 'Win against 3 different opponents',
    color: 'rose',
  },
  zhuangzi: {
    id: 'zhuangzi',
    name: 'Zhuangzi',
    subtitle: 'The Relativist',
    difficulty: 'harder',
    description: 'Challenges distinctions, celebrates spontaneity, and questions perspectives',
    debatingStyle: 'Thought experiments, relativism, butterfly dream paradoxes, playful wisdom',
    unlockCriteria: 'Score 8+ in a single round',
    color: 'sky',
  },
  nagarjuna: {
    id: 'nagarjuna',
    name: 'Nagarjuna',
    subtitle: 'The Dialectician',
    difficulty: 'harder',
    description: 'Deconstructs all positions through emptiness doctrine and the Middle Way',
    debatingStyle: 'Logical deconstruction, emptiness arguments, negation of extremes',
    unlockCriteria: 'Win 4 games total',
    color: 'fuchsia',
  },
  shankara: {
    id: 'shankara',
    name: 'Adi Shankara',
    subtitle: 'The Non-Dualist',
    difficulty: 'harder',
    description: 'Teaches Advaita Vedanta: ultimate reality is non-dual consciousness (Brahman)',
    debatingStyle: 'Non-dual logic, Maya (illusion) arguments, scriptural reasoning',
    unlockCriteria: 'Score 25+ in a single game (win)',
    color: 'amber',
  },
  dogen: {
    id: 'dogen',
    name: 'Dōgen',
    subtitle: 'The Zen Master',
    difficulty: 'harder',
    description: 'Teaches being-time, just-sitting, and the unity of practice and enlightenment',
    debatingStyle: 'Zen paradoxes, experiential wisdom, practice-realization unity',
    unlockCriteria: 'Win 5 games total',
    color: 'stone',
  },
};

// Default personality for new players
export const DEFAULT_PERSONALITY: AIPersonality = 'socrates';

// Configurable preparation time (default: 90 seconds)
// Users can adjust this for faster/slower gameplay
export const PREPARATION_TIME = 90;

// Generate preparation time schedule that scales proportionally
// Rounds get progressively shorter: 100% → 83% → 67% → 67% → 50%
export const getPrepTimeSchedule = (baseTime: number = PREPARATION_TIME): number[] => {
  const multipliers = [1.0, 0.83, 0.67, 0.67, 0.50];
  return multipliers.map(mult => Math.round(baseTime * mult));
};

// Default schedule (for 90s base time)
export const PREP_TIME_SCHEDULE = getPrepTimeSchedule(PREPARATION_TIME);

// AI "thinking" delays for immersion (fixed, not scaled by timer)
// Easy: instant, Medium: 3s, Hard: 5s
export const AI_THINKING_DELAY = {
  easy: 0,
  medium: 3,
  hard: 5
};

export const WINNING_SCORE = 25;
export const MAX_PAUSES_PER_PLAYER = 2; // Number of times a player can pause during a game

// UI interaction delays (milliseconds)
export const SUBMIT_BUTTON_COOLDOWN_MS = 1000; // Debounce delay for submit button
export const SUBMISSION_LOCK_RESET_MS = 100; // Delay before releasing submission lock
export const ARIA_ANNOUNCEMENT_CLEAR_MS = 1000; // Delay before clearing ARIA announcements

// GDD Appendix A: Principle Card Deck (Curated Sample)
import principles from './public/principles.json';
export const PRINCIPLE_DECK: Principle[] = principles as Principle[];

// Round and difficulty schedules
export const MAX_ROUNDS = 5;
export const TWIST_SCHEDULE = [0.2, 0.3, 0.4, 0.5, 0.6];
export const AI_DIFFICULTY_SCHEDULE: Difficulty[] = ['easier','moderate','moderate','harder','harder'];
